import { useState } from 'react';
import { X, CalendarClock, AlertTriangle } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { localInputToIso, isoToLocalInput } from '@/lib/student-objectives/deadline-format';

interface ScheduleModalProps {
  /** Nombre de diffusions sélectionnées (titre). */
  count: number;
  /** Vrai si une cible blog Instagram/LinkedIn est sélectionnée (carrousel requis). */
  needsCarousel: boolean;
  pending: boolean;
  onClose: () => void;
  onConfirm: (scheduledAtIso: string) => void;
}

/**
 * Modale de programmation : choisit une date/heure (datetime-local) → ISO.
 * Avertit quand une cible blog IG/LinkedIn requiert un carrousel pré-généré.
 */
export function ScheduleModal({
  count,
  needsCarousel,
  pending,
  onClose,
  onConfirm,
}: ScheduleModalProps) {
  // Pré-rempli à +1h, arrondi à l'heure (valeur d'input locale).
  const [value, setValue] = useState(() => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setMinutes(0, 0, 0);
    return isoToLocalInput(d.toISOString());
  });

  const iso = localInputToIso(value);
  const isFuture = iso !== null && new Date(iso).getTime() > Date.now();

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-t-3xl bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-xl sm:rounded-3xl sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <CalendarClock className="text-primary-600 h-5 w-5" />
            <h2 className="font-display text-ink-900 text-lg font-extrabold">
              Programmer {count} diffusion{count > 1 ? 's' : ''}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-ink-400 hover:text-ink-700 hover:bg-ink-100 -mr-1 -mt-1 flex h-9 w-9 items-center justify-center rounded-full"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <label className="font-display text-ink-700 mb-1.5 block text-xs font-bold uppercase tracking-wide">
          Date et heure de publication
        </label>
        <input
          type="datetime-local"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="border-ink-200 focus:border-primary-500 focus:ring-primary-500/30 w-full rounded-xl border bg-white px-3 py-2.5 font-sans text-sm focus:outline-none focus:ring-2"
        />
        {!isFuture && (
          <p className="text-error-600 mt-1.5 font-sans text-xs">Choisis une date dans le futur.</p>
        )}

        {needsCarousel && (
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="font-sans text-xs text-amber-800">
              Instagram et LinkedIn exigent un carrousel généré au préalable dans l'éditeur de
              l'article. Sans carrousel, la publication échouera à l'échéance.
            </p>
          </div>
        )}

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Annuler
          </Button>
          <Button
            onClick={() => iso && onConfirm(iso)}
            disabled={!isFuture || pending}
            loading={pending}
            className={cn(!isFuture && 'pointer-events-none')}
          >
            Programmer
          </Button>
        </div>
      </div>
    </div>
  );
}
